import { Flex, Loading, Table, Text } from '@/components';
import {
  PinpointMarker,
  PinpointMarkerForTableCell,
} from '@/features/comment/components/pinpoint';
import { pinpointTableActionColumn } from '@/features/comment/components/pinpoint/PinpointTableRowMenuCell';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import {
  getPinpointSceneAndFieldBySheetType,
  getPinpointSheetType,
} from '@/features/comment/utils/pinpoint';
import { useProject } from '@/features/project/_hooks/project';
import { getPaymentAmountBreakdownTableData } from '@/features/project/_utils/payment';
import { isBefore2025 } from '@/features/project/_utils/project';
import { usePaymentGroups } from '@/features/project/projects/detail/payment/_hooks/payment-group';
import { useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import {
  AmountUnitLabelModifier,
  FALLBACK_TEXT,
  formatValueByUnit,
} from '@/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const PaymentAmountBreakdownTable: FC = () => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const paymentGroups = usePaymentGroups({ projectId });
  const commentStackSiderView = useCommentStackSiderView();

  const isKsStandardAfter2025 = useMemo(
    () =>
      project.data?.sheetType === 'KS' &&
      project.data.fundAdditionalAttributes?.fundSheetType === 'standard' &&
      !isBefore2025(project.data),
    [project],
  );

  if (paymentGroups.isPending || !paymentGroups.data) {
    return <Loading />;
  }

  const pinpointSheetType = getPinpointSheetType(
    project.data?.sheetType ?? 'RS',
    project.data?.fundAdditionalAttributes?.fundSheetType,
  );

  const dataSource = getPaymentAmountBreakdownTableData(paymentGroups.data);

  return (
    <Flex vertical gap="small">
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType,
          rs: {
            path: { project: projectId },
            field: 'rs.payment-amount-breakdown-title',
          },
          ss: {
            path: { project: projectId },
            field: 'ss.payment-amount-breakdown-title',
          },
          ksStandard: {
            path: { project: projectId },
            field: 'ks.standard.payment-amount-breakdown-title',
          },
        })}
      >
        <Text
          variant="h3"
          text={
            isKsStandardAfter2025
              ? t(
                  'payment-amount-breakdown-expenditure-from-each-block-in-previous-year',
                )
              : t('payment-amount-breakdown')
          }
        />
      </PinpointMarker>
      <Table
        rowKey="amountBreakdownId"
        dataSource={dataSource}
        columns={[
          {
            title: '',
            key: 'displayCode',
            dataIndex: 'displayCode',
            width: 80,
            render: (value) => <Text weight="regular" text={value} />,
          },
          {
            title: <Text text={t('payment-name')} />,
            key: 'paymentName',
            dataIndex: 'paymentName',
            width: 300,
            render: (_, record) => {
              return <Text weight="regular" text={record.paymentName} />;
            },
          },
          {
            title: <Text text={t('payment-contract-overview')} />,
            key: 'contractOverview',
            dataIndex: 'contractOverview',
            width: 300,
            render: (value) => <Text weight="regular" text={value} />,
          },
          {
            title: <Text text={t('payment-amount-breakdown-name')} />,
            key: 'name',
            dataIndex: 'name',
            onCell: () => ({ style: { minWidth: 88 } }),
            render: (_, record) => {
              const path = {
                project: projectId,
                paymentGroup: record.paymentGroupId,
                paymentId: record.paymentId,
                amountBreakdownName: record.name,
              };
              return (
                <PinpointMarkerForTableCell
                  filteredOrganizationFiscalYear={project.data?.fiscalYear}
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType,
                    rs: {
                      path,
                      field: 'rs.payment-amount-breakdown-row',
                    },
                    ss: {
                      path,
                      field: 'ss.payment-amount-breakdown-row',
                    },
                    ksStandard: {
                      path,
                      field: 'ks.standard.payment-amount-breakdown-row',
                    },
                  })}
                >
                  <Text weight="regular" text={record.name} />
                </PinpointMarkerForTableCell>
              );
            },
          },
          {
            title: <Text text={t('payment-amount-breakdown-purpose')} />,
            key: 'purpose',
            dataIndex: 'purpose',
            render: (value) => <Text weight="regular" text={value} />,
          },
          {
            title: (
              <Text
                text={t('payment-amount-breakdown-amount-with-unit', {
                  unit: AmountUnitLabelModifier[unit],
                })}
              />
            ),
            align: 'right',
            key: 'amount',
            dataIndex: 'amount',
            onCell: () => ({ style: { minWidth: 120 } }),
            render: (value) => {
              return value !== null && value !== undefined
                ? formatValueByUnit(value, { operation: 'round', unit })
                : FALLBACK_TEXT;
            },
          },
          pinpointTableActionColumn<(typeof dataSource)[number]>({
            showWithoutPinpoint: true,
            render: (record) => ({
              pinpoint: {
                project: project.data,
                onPinpoint: () => {
                  const path = {
                    project: projectId,
                    paymentGroup: record.paymentGroupId,
                    paymentId: record.paymentId,
                    amountBreakdownName: record.name,
                  };
                  commentStackSiderView.set({
                    payload: [
                      {
                        siderType: 'comment',
                        config: {
                          viewType: 'comment-form',
                          target: {
                            projectFiscalYear: project.data?.fiscalYear,
                          },
                          isEditing: false,
                          pinpoint: getPinpointSceneAndFieldBySheetType({
                            pinpointSheetType,
                            rs: {
                              path,
                              field: 'rs.payment-amount-breakdown-row',
                            },
                            ss: {
                              path,
                              field: 'ss.payment-amount-breakdown-row',
                            },
                            ksStandard: {
                              path,
                              field: 'ks.standard.payment-amount-breakdown-row',
                            },
                          }),
                        },
                      },
                    ],
                    target: {
                      type: 'project',
                      projectId,
                    },
                  });
                },
              },
            }),
          }),
        ]}
        expandable={{
          defaultExpandAllRows: true,
        }}
        pagination={false}
      />
    </Flex>
  );
};
